import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface ServiceWorkerState {
  waiting: ServiceWorker | null;
  applying: boolean;
}

interface ServiceWorkerActions {
  apply: () => void;
}

interface ServiceWorkerMeta {
  supported: boolean;
}

interface ServiceWorkerContextValue {
  state: ServiceWorkerState;
  actions: ServiceWorkerActions;
  meta: ServiceWorkerMeta;
}

const Context = createContext<ServiceWorkerContextValue | null>(null);

export function ServiceWorkerProvider({ children }: { children: ReactNode }) {
  const supported = !__MOCK__ && 'serviceWorker' in navigator;
  const [waiting, setWaiting] = useState<ServiceWorker | null>(null);
  const [applying, setApplying] = useState(false);
  useEffect(() => {
    if (!supported) return;
    let active = true;
    void navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        if (active) setWaiting(registration.waiting);
        registration.addEventListener('updatefound', () => {
          const worker = registration.installing;
          worker?.addEventListener('statechange', () => {
            if (
              active &&
              worker.state === 'installed' &&
              navigator.serviceWorker.controller
            )
              setWaiting(worker);
          });
        });
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [supported]);
  const apply = useCallback(() => {
    if (!waiting || applying) return;
    setApplying(true);
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      () => window.location.reload(),
      { once: true },
    );
    waiting.postMessage('ACTIVATE_AT_SAFE_POINT');
  }, [applying, waiting]);
  const value = useMemo<ServiceWorkerContextValue>(
    () => ({
      state: { waiting, applying },
      actions: { apply },
      meta: { supported },
    }),
    [apply, applying, supported, waiting],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useServiceWorker() {
  const value = useContext(Context);
  if (!value) throw new Error('Missing service worker provider');
  return value;
}
